import React, { useCallback, useMemo } from 'react';
import { useTemplateOrCalculation } from '../../../hooks/useTemplateOrCalculation';
import { useSelector } from 'react-redux';
import { ReduxState, useAppDispatch } from '../../../store';
import LoadingIndicator from '../../../components/global/LoadingIndicator/LoadingIndicator';
import { evaluationsRowClassName, getBaseExecutionColumns } from './evaluationsLib';
import {
  CalculationStatisticClusterType,
  PackingPictureResponse,
  TargetStoreEvaluationRatesResponse,
} from '@ydistri/api-sdk';
import { useCurrency } from '../../../hooks/useCurrency';
import { DataTable, IconButton, IconButton_IconSizes, Row, Text } from '@ydistri/ds';
import { styled } from 'styled-components';
import { COLORS } from '../../../styles/style';
import {
  EntityColumns,
  EntityColumnsConfig,
  useStoreTableColumnsProvider,
} from '../../../hooks/useEntityTableColumnsProvider';
import { formatPercentage } from '@ydistri/generic-utils';
import { useGetSourceStoreTargetStoresCategoriesEvaluationRatesQuery } from './apiCalculationEvaluations';
import { useApplicationConfiguration } from '../../../hooks/useApplicationConfiguration';
import { useGetCalculationQuery } from '../apiCalculationDetail';
import { AvailableModals, openModal } from '../../Modals/modalSlice';
import { AiFillPicture } from 'react-icons/ai';
import { computeRemSize } from '@ydistri/theme';

const Header = styled.b`
  margin-left: ${computeRemSize(48)};
`;
const TableWrapper = styled.div`
  margin-left: ${computeRemSize(48)};
  margin-right: ${computeRemSize(100)};
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;

  border: 2px solid ${COLORS.BORDER};
`;

interface TargetStoreDetailTableProps {
  sourceStoreId: number;
  departmentId?: number;
}

const TargetStoreDetailTable: React.FC<TargetStoreDetailTableProps> = ({
  sourceStoreId,
  departmentId,
}) => {
  const dispatch = useAppDispatch();
  const templateOrCalculation = useTemplateOrCalculation();
  const { data: calculationData } = useGetCalculationQuery(templateOrCalculation.id);
  const clusterType = useSelector((state: ReduxState) => state.evaluations.clusterType);
  const currencyId = useCurrency();
  const columnsProvider = useStoreTableColumnsProvider();
  const { isEvaluationDeliveryEnabled } = useApplicationConfiguration();

  const { data, isFetching: isFetchingStatistics } =
    useGetSourceStoreTargetStoresCategoriesEvaluationRatesQuery({
      calculationId: templateOrCalculation.id,
      clusterType,
      sourceStoreId,
      departmentId,
    });

  const showBoxPackingPicturesHandler = useCallback(
    (targetStoreId: number, boxPackingPictures: PackingPictureResponse[]) => {
      dispatch(
        openModal({
          type: AvailableModals.BOX_PACKING_PICTURES,
          data: {
            sourceStoreId,
            targetStoreId,
            departmentId,
            boxes: boxPackingPictures,
            calculationId: templateOrCalculation.id,
          },
        }),
      );
    },
    [departmentId, dispatch, sourceStoreId, templateOrCalculation.id],
  );

  const columns = useMemo(() => {
    const columnsConfig: EntityColumnsConfig<TargetStoreEvaluationRatesResponse> = {
      [EntityColumns.ID]: {
        title: 'Receiving Store ID',
        width: computeRemSize(100),
      },
      [EntityColumns.CODE]: {
        title: 'Receiving Store Code',
        width: computeRemSize(100),
      },
      [EntityColumns.NAME]: {
        title: 'Receiving Store Name',
      },
    };
    const storeColumns = columnsProvider<TargetStoreEvaluationRatesResponse>(columnsConfig);

    const columns = getBaseExecutionColumns(currencyId, storeColumns, {
      includeDepartments: clusterType === CalculationStatisticClusterType.StoreDepartment,
      includeRoutes: false,
      includeDeliveredValue: isEvaluationDeliveryEnabled,
    });

    columns.push(
      {
        title: 'Total Picking Positions (PP)',
        dataIndex: ['evaluationSummary', 'totalPickingPositions'],
        key: 'totalPickingPositions',
        align: 'right',
        width: computeRemSize(160),
        render: (value: number) => value.toLocaleString(),
        sorter: (a, b) =>
          a.evaluationSummary.totalPickingPositions - b.evaluationSummary.totalPickingPositions,
      },
      {
        title: 'Shipped PP',
        dataIndex: ['evaluationSummary', 'shippedPickingPositions'],
        key: 'shippedPickingPositions',
        align: 'right',
        width: computeRemSize(160),
        sorter: (a, b) =>
          a.evaluationSummary.shippedPickingPositions - b.evaluationSummary.shippedPickingPositions,
      },
      {
        title: 'Shipped PP [%]',
        dataIndex: ['evaluationSummary', 'shippedPickingPositionsRate'],
        key: 'shippedPickingPositionsRate',
        align: 'right',
        width: computeRemSize(160),
        render: (value: number) => formatPercentage(value * 100, 0),
        sorter: (a, b) =>
          a.evaluationSummary.shippedPickingPositionsRate -
          b.evaluationSummary.shippedPickingPositionsRate,
      },
    );

    if (calculationData?.isPap) {
      columns.push({
        title: 'Box Packing Pictures',
        key: 'packingPictures',
        width: '12rem',
        render: (_, record) => {
          const packingBoxesPictures = record.packingPictures;
          if (packingBoxesPictures && packingBoxesPictures.length > 0) {
            const boxCount = packingBoxesPictures.length;
            const label = boxCount === 1 ? '1 box' : `${boxCount} boxes`;
            return (
              <Row $justifyContent="space-between" $alignItems="center">
                <Text>{label}</Text>
                <IconButton
                  icon={<AiFillPicture size={IconButton_IconSizes.small} />}
                  tooltip="Show packing pictures"
                  onClick={() =>
                    showBoxPackingPicturesHandler(record.store.id, packingBoxesPictures)
                  }
                  size="small"
                />
              </Row>
            );
          }
        },
      });
    }

    return columns;
  }, [
    columnsProvider,
    currencyId,
    clusterType,
    isEvaluationDeliveryEnabled,
    calculationData?.isPap,
    showBoxPackingPicturesHandler,
  ]);

  const rowKeyProvider = useCallback((record: TargetStoreEvaluationRatesResponse) => {
    return record.store.id.toString();
  }, []);

  return (
    <>
      {isFetchingStatistics && <LoadingIndicator />}
      {data && (
        <>
          <Header>Target stores:</Header>
          <TableWrapper>
            <DataTable<TargetStoreEvaluationRatesResponse>
              id={`target-store-evaluations-${sourceStoreId}`}
              dataSource={data}
              columns={columns}
              loading={isFetchingStatistics}
              showHeader={true}
              rowClassName={evaluationsRowClassName}
              rowKey={rowKeyProvider}
            />
          </TableWrapper>
        </>
      )}
    </>
  );
};

export default TargetStoreDetailTable;
